import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import AppSidebar from "@/components/AppSidebar";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import RoleBoard from "@/pages/RoleBoard";
import TalentMarketExplorer from "@/pages/TalentMarketExplorer";
import WorkOrderForm from "@/pages/WorkOrderForm";
import WorkOrderDashboard from "@/pages/WorkOrderDashboard";
import SettingsPage from "@/pages/Settings";
import ClientManagement from "@/pages/admin/ClientManagement";
import WorkOrderQueue from "@/pages/admin/WorkOrderQueue";
import CandidateDatabase from "@/pages/admin/CandidateDatabase";
import CandidateUpload from "@/pages/admin/CandidateUpload";
import CandidateReview from "@/pages/admin/CandidateReview";
import PlatformAnalytics from "@/pages/admin/PlatformAnalytics";
import PasswordReset from "@/pages/PasswordReset";
import HelpCenter from "@/pages/HelpCenter";
import MessagesPage from "@/pages/MessagesPage";
import AdminCommunications from "@/pages/admin/AdminCommunications";
import DemoFunnel from "@/pages/admin/DemoFunnel";

function ProtectedRoute({ children }) {
  const { user, loading, sessionExpired } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100"><p className="text-slate-500 font-accent">Loading...</p></div>;
  if (!user) return <Navigate to={`/login?${sessionExpired ? "session=expired&" : ""}next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading, isAdmin, sessionExpired } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!user) return <Navigate to={`/login?${sessionExpired ? "session=expired&" : ""}next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
}

function SessionExpiredNotice() {
  const { sessionExpired, dismissSessionExpired } = useAuth();
  if (!sessionExpired) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/40 px-4" data-testid="session-expired-overlay" role="dialog" aria-modal="true" aria-labelledby="session-expired-title">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl animate-fadeIn" data-testid="session-expired-modal">
        <p className="text-xs font-mono uppercase tracking-widest text-slate-400">Security timeout</p>
        <h2 id="session-expired-title" className="mt-2 text-2xl font-heading text-slate-900" data-testid="session-expired-title">Session expired</h2>
        <p className="mt-3 text-sm leading-6 text-slate-600" data-testid="session-expired-message">For your security, your session has ended. Please sign in again to continue.</p>
        <Button data-testid="session-expired-sign-in-button" onClick={dismissSessionExpired} className="mt-5 w-full bg-evergreen-950 hover:bg-evergreen-900 text-white">Sign in again</Button>
      </div>
    </div>
  );
}

function LoginRoute() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next") || "/";
  const safeNext = next.startsWith("/") && !next.startsWith("//") ? next : "/";
  if (user) return <Navigate to={safeNext} replace />;
  return <Login />;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100"><p className="text-slate-500 font-accent">Loading...</p></div>;

  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<PasswordReset />} />
      <Route path="/reset-password" element={<PasswordReset />} />
      <Route path="/" element={<ProtectedRoute><AppSidebar /></ProtectedRoute>}>
        <Route index element={<RoleBoard />} />
        <Route path="search/new" element={<TalentMarketExplorer />} />
        <Route path="search/:id" element={<TalentMarketExplorer />} />
        <Route path="search" element={<Navigate to="/search/new" replace />} />
        <Route path="work-orders" element={<WorkOrderDashboard />} />
        <Route path="work-orders/new" element={<WorkOrderForm />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="help" element={<HelpCenter />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="admin/clients" element={<AdminRoute><ClientManagement /></AdminRoute>} />
        <Route path="admin/candidates" element={<AdminRoute><CandidateDatabase /></AdminRoute>} />
        <Route path="admin/candidate-upload" element={<AdminRoute><CandidateUpload /></AdminRoute>} />
        <Route path="admin/candidate-review" element={<AdminRoute><CandidateReview /></AdminRoute>} />
        <Route path="admin/work-orders" element={<AdminRoute><WorkOrderQueue /></AdminRoute>} />
        <Route path="admin/analytics" element={<AdminRoute><PlatformAnalytics /></AdminRoute>} />
        <Route path="admin/communications" element={<AdminRoute><AdminCommunications /></AdminRoute>} />
        <Route path="admin/demo-funnel" element={<AdminRoute><DemoFunnel /></AdminRoute>} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <TooltipProvider>
          <AppRoutes />
          <SessionExpiredNotice />
          <Toaster />
        </TooltipProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
